import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { Button, Card } from './primitives'
import { BUILD, shortLabel } from '@/lib/build'
import { isStaleChunkError, reloadOnceForStaleBuild } from '@/lib/staleChunk'
import { track } from '@/lib/analytics'

type Props = {
  children: ReactNode
  /** Names the area for the message and the analytics event, e.g. "Decisions". */
  area: string
  /** Changing any of these clears the error — used to retry on tab change. */
  resetKeys?: unknown[]
}

type State = { error: Error | null }

/**
 * Catches a render error and confines it to one area.
 *
 * React unmounts the whole tree when a render throws, so without this a single bad field
 * — an unexpected null in one of 347 imported records — takes the entire app to a blank
 * page. That is the worst possible failure for a client-facing tool: it looks like the
 * tool is broken rather than like one record is.
 *
 * Wrapping each tab means the roadmap still works when the field guide throws, and the
 * person can navigate away from the problem instead of reloading and landing on it again.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    /**
     * A chunk that no longer exists is not a fault, it is a deploy. The filenames this tab
     * is asking for were replaced while it sat open, and reloading fixes it every time —
     * so it reloads rather than showing somebody an error page for something that is not
     * wrong. Once per build, or a genuinely broken deploy would loop.
     */
    if (isStaleChunkError(error)) {
      track('app_updated_reload', { area: this.props.area, build: BUILD.sha })
      if (reloadOnceForStaleBuild(BUILD.sha, sessionStorage, () => window.location.reload())) {
        return
      }
    }

    // The build stamp is what makes a screenshot of this actionable — otherwise the first
    // question back is always "which version were you on?"
    console.error(`[${this.props.area}] ${shortLabel()}`, error, info.componentStack)
    track('app_error', {
      area: this.props.area,
      message: error.message.slice(0, 120),
      build: BUILD.sha,
    })
  }

  componentDidUpdate(prev: Props) {
    const changed =
      prev.resetKeys?.length !== this.props.resetKeys?.length ||
      (this.props.resetKeys ?? []).some((k, i) => k !== prev.resetKeys?.[i])
    if (this.state.error && changed) this.setState({ error: null })
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    // The reload is already on its way; saying so beats a flash of an error page that is
    // about to disappear anyway.
    if (isStaleChunkError(error)) {
      return (
        <Card>
          <div className="flex items-start gap-3 py-4">
            <RefreshCw size={20} className="mt-0.5 shrink-0 animate-spin text-mc-primary" aria-hidden />
            <div>
              <h2 className="text-base font-medium text-mc-ink">Updating to the new version…</h2>
              <p className="mt-1 text-sm text-mc-body">
                The tracker was updated while this tab was open. It is reloading itself — if
                nothing happens in a moment,{' '}
                <button type="button" className="underline"
                        onClick={() => window.location.reload()}>
                  reload the page
                </button>.
              </p>
            </div>
          </div>
        </Card>
      )
    }

    return (
      <Card>
        <div className="flex flex-col items-start gap-3 py-4 sm:flex-row">
          <AlertTriangle size={22} className="shrink-0 text-mc-critical" aria-hidden />
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-medium text-mc-ink">
              Something went wrong in {this.props.area}
            </h2>
            <p className="mt-1 text-sm text-mc-body">
              The rest of the tracker still works — switch to another tab and carry on.
              Nothing you had saved is affected.
            </p>

            <details className="mt-3">
              <summary className="cursor-pointer text-xs text-mc-muted hover:text-mc-primary">
                Technical detail
              </summary>
              <pre className="mt-2 max-h-40 overflow-auto rounded bg-mc-surface-2 p-3 font-mono text-xs whitespace-pre-wrap text-mc-body">
                {error.message}
              </pre>
              <p className="mt-1.5 font-mono text-xs text-mc-muted">{shortLabel()}</p>
            </details>

            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="primary" onClick={() => this.setState({ error: null })}>
                Try again
              </Button>
              <Button onClick={() => window.location.reload()}>Reload the page</Button>
            </div>
          </div>
        </div>
      </Card>
    )
  }
}
